import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../firebase';
import HairstylistList from '../components/HairstylistList';
import { collection as getCollection, getDocs as getAllDocs } from 'firebase/firestore';
import { useTranslation } from '../hooks/useTranslation';
import styles from './SearchPage.module.css';
import { createKidsServiceCodeSet, hasKidsServicesFromCodes } from '../utils/kidsServices';

function FavoritesPage() {
  const { currentUser } = useAuth();
  const { language } = useTranslation();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        // Récupérer les favoris
        const favoritesQuery = query(
          collection(db, 'favorites'),
          where('userId', '==', currentUser.uid),
        );
        const favoritesSnapshot = await getDocs(favoritesQuery);
        const favoriteIds = favoritesSnapshot.docs.map((doc) => doc.data().hairstylistId);

        // Récupérer les données des coiffeuses favorites
        const favoritesData = await Promise.all(
          favoriteIds.map(async (id) => {
            const stylistDoc = await getDoc(doc(db, 'hairstylists', id));
            return stylistDoc.exists() ? { id: stylistDoc.id, ...stylistDoc.data() } : null;
          }),
        );
        // Récupérer les services et catégories pour le lookup
        const [servicesSnap, categoriesSnap] = await Promise.all([
          getAllDocs(getCollection(db, 'services')),
          getAllDocs(getCollection(db, 'categories')),
        ]);

        const servicesData = servicesSnap.docs
          .map((doc) => {
          const data = doc.data();
          return {
            ...data,
            code: data.code || data.prestationCode || '',
            label: data.label || data.prestation || '',
          };
          })
          .filter((s) => s.code && s.label);

        const categoriesData = categoriesSnap.docs
          .map((doc) => doc.data())
          .sort((a, b) => (a.label || '').localeCompare(b.label || ''));

        const kidsServiceCodeSet = createKidsServiceCodeSet(servicesData, categoriesData);
        const normalizedFavorites = favoritesData
          .filter(Boolean)
          .map((stylist) => ({
            ...stylist,
            hasKidsServices:
              stylist?.hasKidsServices ??
              hasKidsServicesFromCodes(stylist?.services || [], kidsServiceCodeSet),
          }));

        setFavorites(normalizedFavorites);
        setServices(servicesData);
        setCategories(categoriesData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching favorites:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, navigate]);

  const serviceLabelLookup = useMemo(
    () =>
      services.reduce((acc, service) => {
        // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
        let label = service.label || service.prestation || '';
        if (language && language !== 'fr' && service.en) {
          label = service.en;
        }
        acc[service.code] = label;
        return acc;
      }, {}),
    [services, language],
  );

  const serviceCategoryLookup = useMemo(
    () =>
      services.reduce((acc, service) => {
        const category = categories.find((category) => category.categoryCode === service.categoryCode);
        // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
        let categoryLabel =
          category?.label ||
          service.category ||
          service.categoryLabel ||
          service.categoryCode ||
          'Autres prestations';
        if (language && language !== 'fr' && category?.en) {
          categoryLabel = category.en;
        }
        acc[service.code] = {
          label: categoryLabel,
          code: service.categoryCode || categoryLabel,
        };
        return acc;
      }, {}),
    [services, categories, language],
  );

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Chargement...</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.pageTitle}>Ma liste de favoris</h1>
        {favorites.length > 0 ? (
          <HairstylistList
            hairstylists={favorites}
            loading={false}
            error={null}
            hasMore={false}
            loadMore={() => {}}
            serviceLookup={serviceLabelLookup}
            serviceCategoryLookup={serviceCategoryLookup}
          />
        ) : (
          <p className={styles.empty}>Aucun favori pour le moment</p>
        )}
      </div>
    </div>
  );
}

export default FavoritesPage;

